/**
 * Composites layer buffers + overlays onto the visible canvas.
 * Handles zoom, scroll, and layer opacity.
 */
export class ViewportCompositor {
  constructor(state, mapCanvas, canvas) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this._overlays = []; // Array of { render(ctx, state, viewport) }
    this._rafId = null;
    this._dirty = true;
  }

  addOverlay(overlay) {
    this._overlays.push(overlay);
  }

  /** Request a repaint on next frame. */
  requestRender() {
    this._dirty = true;
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (this._dirty) this.render();
    });
  }

  /** Force immediate render. */
  render() {
    this._dirty = false;
    const ctx = this.ctx;
    const state = this.state;
    const zoom = state.get('zoom');
    const scrollX = state.get('scrollX');
    const scrollY = state.get('scrollY');
    const layers = state.get('layers');

    // Resize canvas to fill wrapper
    const wrapper = this.canvas.parentElement;
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect();
      if (this.canvas.width !== rect.width || this.canvas.height !== rect.height) {
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;
      }
    }

    const w = this.canvas.width;
    const h = this.canvas.height;

    // Clear
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.translate(-scrollX, -scrollY);
    ctx.scale(zoom, zoom);

    // Draw checkerboard background for map area
    const mapW = state.get('mapWidth') * state.get('tileWidth');
    const mapH = state.get('mapHeight') * state.get('tileHeight');
    this._drawCheckerboard(ctx, mapW, mapH);

    // Calculate visible tile bounds (with 1-tile padding for partial tiles at edges)
    const tileW = state.get('tileWidth');
    const tileH = state.get('tileHeight');
    const mapTilesW = state.get('mapWidth');
    const mapTilesH = state.get('mapHeight');
    const tileBounds = {
      minTileX: Math.max(0, Math.floor(scrollX / (tileW * zoom)) - 1),
      minTileY: Math.max(0, Math.floor(scrollY / (tileH * zoom)) - 1),
      maxTileX: Math.min(mapTilesW - 1, Math.ceil((scrollX + w) / (tileW * zoom)) + 1),
      maxTileY: Math.min(mapTilesH - 1, Math.ceil((scrollY + h) / (tileH * zoom)) + 1),
    };

    // Composite each visible layer (viewport-culled, offset-aware)
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      if (!layer.visible) continue;

      if (layer.type === 'tilelayer') {
        // Compute per-layer bounds accounting for layer offset
        const ox = layer.offsetX || 0;
        const oy = layer.offsetY || 0;
        const layerBounds = (ox === 0 && oy === 0) ? tileBounds : {
          minTileX: Math.max(0, Math.floor((scrollX / zoom - ox) / tileW) - 1),
          minTileY: Math.max(0, Math.floor((scrollY / zoom - oy) / tileH) - 1),
          maxTileX: Math.min(mapTilesW - 1, Math.ceil(((scrollX + w) / zoom - ox) / tileW) + 1),
          maxTileY: Math.min(mapTilesH - 1, Math.ceil(((scrollY + h) / zoom - oy) / tileH) + 1),
        };
        const result = this.mapCanvas.renderViewport(i, layerBounds);
        if (result) {
          ctx.globalAlpha = layer.opacity ?? 1;
          ctx.drawImage(result.canvas, result.originX + ox, result.originY + oy);
          ctx.globalAlpha = 1;
        }
      }
    }

    // Draw overlays (grid, cursor, properties, etc.)
    const viewport = { scrollX, scrollY, zoom, width: w, height: h, tileBounds };
    for (const overlay of this._overlays) {
      overlay.render(ctx, state, viewport);
    }

    ctx.restore();
  }

  _drawCheckerboard(ctx, width, height) {
    // Just draw the map-area background — the checkerboard detail is barely
    // visible and was the most expensive part for large maps. A solid color
    // with a subtle tone difference is sufficient.
    ctx.fillStyle = '#2a2a3e';
    ctx.fillRect(0, 0, width, height);
  }

  /** Convert screen coordinates to tile coordinates. */
  screenToTile(screenX, screenY) {
    const zoom = this.state.get('zoom');
    const scrollX = this.state.get('scrollX');
    const scrollY = this.state.get('scrollY');
    const tileW = this.state.get('tileWidth');
    const tileH = this.state.get('tileHeight');

    const worldX = (screenX + scrollX) / zoom;
    const worldY = (screenY + scrollY) / zoom;

    return {
      x: Math.floor(worldX / tileW),
      y: Math.floor(worldY / tileH),
    };
  }

  destroy() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
  }
}
